import { useQuery } from '@tanstack/react-query'
import { getAllTenantOrders, getOrderSupplierChanges, getSupplierChanges } from '../services/auth-api'
import type { SupplierChangesQuery } from '../services/auth-api'

const SUPPLIER_CHANGES_KEY = ['orders', 'supplier-changes'] as const
// Options shared by both lists, like the other order lists: refetch when the
// admin comes back to the tab.
const LIVE_LIST_OPTIONS = { refetchOnWindowFocus: true, staleTime: 30 * 1000 }

/**
 * How many orders have supplier changes, and each supplier's heading figures
 * (PO-008 AC3). Asks for a single row: only the totals are used.
 */
export function useSupplierChangesSummary(search: string, enabled = true) {
  return useQuery({
    queryKey: [...SUPPLIER_CHANGES_KEY, 'summary', search],
    queryFn: () => getSupplierChanges({ search: search || undefined, page: 1, pageSize: 1 }),
    enabled,
    ...LIVE_LIST_OPTIONS,
  })
}

/**
 * One supplier's orders with changes, the oldest change first. `limit` grows
 * as the admin asks for more; the rows already shown stay while it loads.
 */
export function useSupplierGroupOrders(supplierId: number | null, search: string, limit: number, enabled: boolean) {
  return useQuery({
    queryKey: [...SUPPLIER_CHANGES_KEY, 'group', supplierId, search, limit],
    queryFn: () => getSupplierChanges({ search: search || undefined, supplierId, page: 1, pageSize: limit }),
    enabled,
    // Keep the rows on screen only while "Show more" loads, not across a new
    // search (that shows a fresh loading state instead of old rows).
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey ?? []
      return previousKey[3] === supplierId && previousKey[4] === search ? previousData : undefined
    },
    ...LIVE_LIST_OPTIONS,
  })
}

/** One order's supplier changes, for its page (PO-009). */
export function useOrderSupplierChanges(orderId: string | undefined) {
  return useQuery({
    queryKey: [...SUPPLIER_CHANGES_KEY, 'order', orderId],
    queryFn: () => getOrderSupplierChanges(orderId as string),
    enabled: Boolean(orderId),
    // A missing or someone else's order is a 404: asking again won't change it.
    retry: false,
    ...LIVE_LIST_OPTIONS,
  })
}

/**
 * One page of the "Supplier changes to review" list (PO-008), each supplier's
 * orders kept together under its heading. The rows on screen stay while only
 * the page changes.
 */
export function useSupplierChangesPage(query: SupplierChangesQuery) {
  return useQuery({
    queryKey: [...SUPPLIER_CHANGES_KEY, 'page', query],
    queryFn: () => getSupplierChanges(query),
    placeholderData: (previousData, previousQuery) => {
      const previous = previousQuery?.queryKey[3] as SupplierChangesQuery | undefined
      const isSameExceptPage =
        previous !== undefined &&
        previous.search === query.search &&
        previous.supplierId === query.supplierId &&
        previous.pageSize === query.pageSize
      return isSameExceptPage ? previousData : undefined
    },
    ...LIVE_LIST_OPTIONS,
  })
}

/** Every company's orders for the SaaS admin's All orders tab (PO-008 AC1). */
export function useAllTenantOrders(query: { search?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: ['orders', 'all-tenants', query],
    queryFn: () => getAllTenantOrders(query),
    // Keep the rows on screen while only the page changes.
    placeholderData: (previousData, previousQuery) => {
      const previous = previousQuery?.queryKey[2] as typeof query | undefined
      return previous?.search === query.search && previous?.pageSize === query.pageSize ? previousData : undefined
    },
    ...LIVE_LIST_OPTIONS,
  })
}
